import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { fmtMXN, fmtDate, downloadBlob } from "../lib/api";
import Topbar from "../components/Topbar";
import StatusBadge from "../components/StatusBadge";
import PizzaSpinner from "../components/PizzaSpinner";
import { ArrowLeft, Building2, Mail, Phone, Banknote, FileText, ShoppingCart, CreditCard, Download, Tag, Pencil, Plus, Save, X as XIcon, History, TrendingUp, TrendingDown, Copy, RefreshCw, Clock, ShieldCheck, CheckCircle2, XCircle, AlertTriangle, Store, Trash2, EyeOff, BellOff } from "lucide-react";
import BranchOperationModal from "../components/BranchOperationModal";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useTranslation } from "react-i18next";
import SupplierEditModal from "../components/SupplierEditModal";
import SupplierSupplyModal from "../components/SupplierSupplyModal";

const DOC_TYPES = ["csf", "official_id", "proof_of_address", "bank_statement", "sat_compliance", "acta_constitutiva", "poder_legal"];

const FieldRow = ({ label, value, mono = false, wide = false }) => (
  <div className={wide ? "sm:col-span-2" : ""}>
    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    <div className={`text-sm whitespace-pre-line ${mono ? "font-mono" : ""} ${value ? "text-[#1A1A1A]" : "text-slate-400"}`}>{value || "—"}</div>
  </div>
);

const SectionCard = ({ title, icon: Icon, children, testid }) => (
  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
    data-testid={testid} className="bg-white rounded-2xl border border-slate-200 p-6">
    <div className="flex items-center gap-2 pb-3 mb-4 border-b border-slate-100">
      {Icon && <Icon className="w-4 h-4 text-[#1A1A1A]" />}
      <h3 className="font-display font-bold text-base text-[#1A1A1A]">{title}</h3>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
  </motion.div>
);

const maskClabe = (v) => {
  if (!v) return "";
  const str = String(v);
  return str.length <= 4 ? str : "•".repeat(str.length - 4) + str.slice(-4);
};

const branchCode = (b) => (b.code || (b.pos_branch_code || "").split("-")[0] || "").toUpperCase();

export default function SupplierDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [supplier, setSupplier] = useState(null);
  const [statement, setStatement] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [errorId, setErrorId] = useState(null);
  const [tab, setTab] = useState("info");
  const [supplies, setSupplies] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [completionToken, setCompletionToken] = useState(null);
  const [branches, setBranches] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editTab, setEditTab] = useState("identity");
  const [taxRegimes, setTaxRegimes] = useState({});
  const [rejectDoc, setRejectDoc] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [validating, setValidating] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [branchOpModal, setBranchOpModal] = useState(undefined); // undefined=closed, null=new, {..}=editing
  const [presetBranchId, setPresetBranchId] = useState(null);
  const [showInactiveOps, setShowInactiveOps] = useState(() => localStorage.getItem("chizy_show_inactive_branch_ops") === "1");
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    api.get("/sat-tax-regimes").then((r) => setTaxRegimes(r.data?.regimes || {})).catch(() => {});
  }, []);

  // Abrir el editor en la solapa "Régimen fiscal" cuando se llega con ?edit=fiscal
  // (link desde el banner de retención en /facturas).
  useEffect(() => {
    if (searchParams.get("edit") === "fiscal" && supplier && user?.role === "corporate_admin") {
      setEditTab("fiscal");
      setEditOpen(true);
      searchParams.delete("edit");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, supplier, user]);

  // Auto-abrir el modal de "Agregar operación" cuando se llega desde el alta
  // de proveedor con ?configure_branch_ops=1 (admin elegió "cada sucursal
  // distinta" en el modal de creación).
  useEffect(() => {
    if (searchParams.get("configure_branch_ops") === "1" && supplier && user?.role === "corporate_admin") {
      const firstMissing = (supplier.missing_branch_ids || [])[0] || null;
      setPresetBranchId(firstMissing);
      setBranchOpModal(null);
      searchParams.delete("configure_branch_ops");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, supplier, user]);

  const loadDocuments = () =>
    api.get(`/suppliers/${id}/documents`).then((r) => setDocuments(r.data)).catch(() => {});

  const reloadSupplier = () => {
    api.get(`/suppliers/${id}`)
      .then((r) => { setSupplier(r.data); setCompletionToken(r.data.completion_token || null); })
      .catch(() => {});
    loadDocuments();
  };

  const TABS = [
    { v: "info", l: t("supplierDetail.tabInfo"), icon: Building2 },
    { v: "prices", l: t("supplierDetail.tabPrices"), icon: Tag, corpOnly: true },
    { v: "invoices", l: t("supplierDetail.tabInvoices"), icon: FileText },
    { v: "pos", l: t("supplierDetail.tabPos"), icon: ShoppingCart },
    { v: "payments", l: t("supplierDetail.tabPayments"), icon: CreditCard },
  ];

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        // Esencial: debe cargar para TODOS los roles con acceso a esta página.
        const supRes = await api.get(`/suppliers/${id}`);
        if (!alive) return;
        setSupplier(supRes.data);
        setCompletionToken(supRes.data.completion_token || null);
        setErrorId(null);
        // Opcional: documentos (para treasury puede devolver 403 → no debe romper la página).
        api.get(`/suppliers/${id}/documents`)
          .then((r) => { if (alive) setDocuments(r.data); })
          .catch((e) => { if (e?.response?.status !== 403) console.error("documents", e); });
        // Opcional: estado de cuenta financiero (solo corporate_admin/treasury/dueño).
        // Para legal/branch_manager devuelve 403 → se ignora sin romper la pantalla.
        api.get(`/account-statements/${id}`)
          .then((r) => { if (alive) setStatement(r.data); })
          .catch((e) => { if (e?.response?.status !== 403) console.error("account-statements", e); });
      } catch (e) {
        if (!alive) return;
        console.error("No se pudo cargar el proveedor", e);
        setErrorId(id);
      }
    };
    load();
    return () => { alive = false; };
  }, [id]);

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setBannerDismissed(sessionStorage.getItem(`chizy_dismiss_branchops_banner_${id}`) === "1");
  }, [id]);

  const dismissBranchOpsBanner = () => {
    sessionStorage.setItem(`chizy_dismiss_branchops_banner_${id}`, "1");
    setBannerDismissed(true);
  };

  const configureBranchOpsNow = () => {
    const firstMissing = (s.missing_branch_ids || [])[0] || null;
    setPresetBranchId(firstMissing);
    setBranchOpModal(null);
    setTimeout(() => {
      document.querySelector('[data-testid="supplier-section-branch-ops"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const loadPriceData = async () => {
    if (user?.role !== "corporate_admin") return;
    const [s, a] = await Promise.all([
      api.get(`/suppliers/${id}/supplies`),
      api.get(`/price-audit-log?supplier_id=${id}`),
    ]);
    setSupplies(s.data);
    setAuditLogs(a.data);
  };

  useEffect(() => {
    if (tab !== "prices" || user?.role !== "corporate_admin") return;
    let alive = true;
    (async () => {
      const [s, a] = await Promise.all([
        api.get(`/suppliers/${id}/supplies`),
        api.get(`/price-audit-log?supplier_id=${id}`),
      ]);
      if (alive) { setSupplies(s.data); setAuditLogs(a.data); }
    })();
    return () => { alive = false; };
  }, [tab, id, user?.role]);

  const startEdit = (rel) => {
    setEditing({
      supply_id: rel.supply_id,
      supply_name: rel.supply.name,
      current_price: rel.unit_price,
      new_price: rel.unit_price,
      reason: "",
    });
  };
  const cancelEdit = () => setEditing(null);
  const unlinkSupply = async (rel) => {
    if (!window.confirm(t("catalog.unlinkConfirmCorp"))) return;
    try {
      await api.delete(`/suppliers/${id}/supplies/${rel.supply_id}`);
      toast.success(t("catalog.productUnlinked"));
      loadPriceData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };
  const saveEdit = async () => {
    if (Math.abs(editing.new_price - editing.current_price) < 0.001) {
      toast.error(t("supplierDetail.samePriceError")); return;
    }
    if (!editing.reason.trim()) { toast.error(t("supplierDetail.reasonRequired")); return; }
    try {
      await api.put(`/suppliers/${id}/supplies/${editing.supply_id}/price`, {
        unit_price: Number(editing.new_price),
        reason: editing.reason.trim(),
      });
      toast.success(t("supplierDetail.priceUpdated"));
      setEditing(null);
      loadPriceData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const loading = errorId !== id && (!supplier || supplier.id !== id);
  const loadError = errorId === id;

  if (loading) return <><Topbar title={t("supplierDetail.title")} /><PizzaSpinner /></>;

  if (loadError || !supplier) {
    return (
      <>
        <Topbar title={t("supplierDetail.title")} />
        <div className="p-6">
          <button onClick={() => navigate(-1)} className="text-sm font-semibold text-slate-500 hover:text-[#1A1A1A] flex items-center gap-1.5 mb-4">
            <ArrowLeft className="w-4 h-4" /> {t("common.back")}
          </button>
          <div data-testid="supplier-detail-load-error" className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <p className="text-sm font-semibold text-red-800">{t("supplierDetail.loadError")}</p>
          </div>
        </div>
      </>
    );
  }

  const s = supplier;
  const invoices = statement?.invoices || [];
  const purchaseOrders = statement?.purchase_orders || [];
  const isPendingDocs = s.status === "pending_documents";
  const copyCompletionLink = () => {
    if (!completionToken) { toast.error(t("common.error")); return; }
    navigator.clipboard?.writeText(`${window.location.origin}/completar-registro/${completionToken}`);
    toast.success(t("suppliers.completionLinkCopied"));
  };
  const regenerateLink = async () => {
    try {
      const r = await api.post(`/admin/suppliers/${id}/regenerate-completion-token`);
      setCompletionToken(r.data.completion_token);
      navigator.clipboard?.writeText(r.data.completion_url);
      toast.success(t("suppliers.regenerateLinkDone"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };
  const exportBranchOpsXlsx = async () => {
    try {
      const r = await api.get(`/suppliers/${id}/branch-operations/export.xlsx`, { responseType: "blob" });
      downloadBlob(r.data, `operaciones_${s?.business_name || id}.xlsx`);
      toast.success(t("export.success"));
    } catch {
      toast.error(t("export.error"));
    }
  };
  const toggleShowInactiveOps = () => {
    setShowInactiveOps((v) => {
      localStorage.setItem("chizy_show_inactive_branch_ops", !v ? "1" : "0");
      return !v;
    });
  };
  const deactivateBranchOp = async (op, branchLabel) => {
    if (!window.confirm(t("supplierDetail.branchOpsCols.deleteConfirm", { branch: branchLabel }))) return;
    try {
      await api.post(`/suppliers/${id}/branch-operations/${op.id}/deactivate`);
      toast.success(t("supplierEdit.branchOps.saved"));
      reloadSupplier();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };
  const viewDoc = async (docId) => {
    try {
      const r = await api.get(`/uploads/supplier-document/${docId}`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch {
      toast.error(t("common.error"));
    }
  };
  const validateDoc = async (doc, approved, reason) => {
    setValidating(doc.id);
    try {
      const r = await api.post(`/suppliers/${id}/documents/${doc.id}/validate`, {
        approved,
        rejection_reason: reason || null,
      });
      await loadDocuments();
      api.get(`/suppliers/${id}`).then((res) => setSupplier(res.data)).catch(() => {});
      if (approved && r.data.legal_validated) toast.success(t("legalValidation.completeToast"));
      else toast.success(approved ? t("legalValidation.approvedToast") : t("legalValidation.rejectedToast"));
      setRejectDoc(null);
      setRejectReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setValidating(null);
    }
  };
  const boolLabel = (v) => (v === true ? t("common.yes") : v === false ? t("common.no") : null);
  const isServ = (s.supplier_type || "insumos") === "servicios";
  const docByType = Object.fromEntries((s.documents || []).map((d) => [d.doc_type, d]));
  const docsByTypeFull = Object.fromEntries(documents.map((d) => [d.doc_type, d]));
  const authorizedBranches = branches.filter((b) => (s.branches_authorized || []).includes(b.id));
  const canViewDocs = user?.role === "corporate_admin" || user?.role === "legal" || user?.role === "hr_admin";
  const canValidate = user?.role === "legal" || user?.role === "corporate_admin" || user?.role === "hr_admin";
  const requiredDocTypes = ["csf", "official_id", "proof_of_address", "bank_statement"];
  const optionalMoralDocs = ["acta_constitutiva", "poder_legal"];
  // Fix WH65 (2026-09-28): RFC de 12 caracteres = persona moral, 13 = física
  // (mismo criterio que el backend). Antes el acta y el poder decían "No cargado
  // (persona física)" para todos, también para las personas morales.
  const rfcLen = (s.rfc || "").trim().length;
  const esMoral = rfcLen === 12;
  const legalDocTypes = [...requiredDocTypes, "sat_compliance", ...optionalMoralDocs];
  const hasRejected = documents.some((d) => d.status === "rejected");
  const totalUnpaid = invoices.filter((i) => i.status !== "paid").reduce((a, b) => a + b.total, 0);
  const totalPaid = invoices.filter((i) => i.status === "paid").reduce((a, b) => a + b.total, 0);
  const payments = invoices.filter((i) => i.payment).map((i) => ({ ...i.payment, invoice: i }));
  const visibleTabs = TABS.filter((tb) => !tb.corpOnly || user?.role === "corporate_admin");

  return (
    <>
      <Topbar title={s.business_name} subtitle={t("supplierDetail.subtitle", { rfc: s.rfc, days: s.credit_days })} />
      <div className="p-6 space-y-5">
        <button onClick={() => navigate(-1)} className="text-sm font-semibold text-slate-500 hover:text-[#1A1A1A] flex items-center gap-1.5">
          <ArrowLeft className="w-4 h-4" /> {t("common.back")}
        </button>

        {user?.role === "corporate_admin" && (
          <div className="flex justify-end -mt-2">
            <button data-testid="supplier-edit-btn" onClick={() => setEditOpen(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#1A1A1A] hover:bg-black text-white rounded-xl text-sm font-bold">
              <Pencil className="w-4 h-4" /> {t("supplierEdit.editButton")}
            </button>
          </div>
        )}

        {isPendingDocs && (
          <div data-testid="supplier-pending-docs-banner"
            className="bg-orange-50 border border-orange-200 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-700 flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display font-bold text-base text-orange-900">{t("suppliers.pendingDocsTitle")}</div>
                <p className="text-xs text-orange-800 mt-1 leading-relaxed max-w-2xl">{t("suppliers.pendingDocsBanner")}</p>
              </div>
            </div>
            {user?.role === "corporate_admin" && (
              <div className="flex gap-2 flex-shrink-0">
                <button data-testid="supplier-copy-link-btn" onClick={copyCompletionLink}
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#1A1A1A] text-white rounded-lg text-xs font-bold hover:bg-black">
                  <Copy className="w-3.5 h-3.5" /> {t("suppliers.copyCompletionLink")}
                </button>
                <button data-testid="supplier-regenerate-link-btn" onClick={regenerateLink}
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-orange-300 text-orange-800 rounded-lg text-xs font-bold hover:bg-orange-100">
                  <RefreshCw className="w-3.5 h-3.5" /> {t("suppliers.regenerateLink")}
                </button>
              </div>
            )}
          </div>
        )}

        {s.needs_branch_operations_setup && !bannerDismissed && (
          <div data-testid="branch-ops-setup-banner"
            className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                <Store className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display font-bold text-base text-amber-900">
                  {t("supplierDetail.branchOpsBanner.title", { count: (s.branches_authorized || []).length })}
                </div>
                <p className="text-xs text-amber-800 mt-1 leading-relaxed max-w-2xl">{t("supplierDetail.branchOpsBanner.body")}</p>
              </div>
            </div>
            {user?.role === "corporate_admin" && (
              <div className="flex gap-2 flex-shrink-0">
                <button data-testid="branch-ops-banner-configure-btn" onClick={configureBranchOpsNow}
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#1A1A1A] text-white rounded-lg text-xs font-bold hover:bg-black">
                  <Store className="w-3.5 h-3.5" /> {t("supplierDetail.branchOpsBanner.configureNow")}
                </button>
                <button data-testid="branch-ops-banner-remind-later-btn" onClick={dismissBranchOpsBanner}
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-amber-300 text-amber-800 rounded-lg text-xs font-bold hover:bg-amber-100">
                  <BellOff className="w-3.5 h-3.5" /> {t("supplierDetail.branchOpsBanner.remindLater")}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("supplierDetail.kpiUnpaid")}</div>
            <div className="text-xl font-display font-extrabold text-[#1A1A1A] mt-1">{fmtMXN(totalUnpaid)}</div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("supplierDetail.kpiPaidHistoric")}</div>
            <div className="text-xl font-display font-extrabold text-emerald-700 mt-1">{fmtMXN(totalPaid)}</div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("supplierDetail.kpiInvoices")}</div>
            <div className="text-xl font-display font-extrabold mt-1">{invoices.length}</div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("supplierDetail.kpiPos")}</div>
            <div className="text-xl font-display font-extrabold mt-1">{purchaseOrders.length}</div>
          </div>
        </div>

        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1 w-fit overflow-x-auto">
          {visibleTabs.map((tb) => (
            <button key={tb.v} data-testid={`supplier-tab-${tb.v}`} onClick={() => setTab(tb.v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition whitespace-nowrap ${
                tab === tb.v ? "bg-[#1A1A1A] text-white" : "text-slate-600 hover:bg-slate-100"
              }`}>
              <tb.icon className="w-3.5 h-3.5" /> {tb.l}
            </button>
          ))}
        </div>

        {tab === "info" && (
          <div className="space-y-4">
            {canValidate && (
              <div data-testid="legal-validation-panel" className="bg-white rounded-2xl border-2 border-[#1A1A1A]/10 p-6">
                <div className="flex items-center gap-2 pb-3 mb-4 border-b border-slate-100">
                  <ShieldCheck className="w-4 h-4 text-[#1A1A1A]" />
                  <h3 className="font-display font-bold text-base text-[#1A1A1A]">{t("legalValidation.title")}</h3>
                </div>

                {/* Fix WH65 (2026-09-28): persona moral aprobada sin acta o poder. */}
                {s.legal_validated && s.legal_parcial ? (
                  <div data-testid="legal-status-banner" className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <div className="text-sm">
                      <span className="font-bold text-amber-800">{t("legalValidation.partialBanner")}</span>
                      {s.legal_validated_at && <span className="text-amber-700"> · {fmtDate(s.legal_validated_at)}</span>}
                      <div data-testid="legal-partial-missing" className="text-xs text-amber-800 mt-0.5">
                        {t("legalValidation.partialMissing", { docs: (s.legal_faltantes || []).join(", ") })}
                      </div>
                    </div>
                  </div>
                ) : s.legal_validated ? (
                  <div data-testid="legal-status-banner" className="flex items-center gap-2.5 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 mb-4">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <div className="text-sm">
                      <span className="font-bold text-emerald-800">{t("legalValidation.approvedBanner")}</span>
                      {s.legal_validated_at && <span className="text-emerald-700"> · {fmtDate(s.legal_validated_at)}</span>}
                    </div>
                  </div>
                ) : hasRejected ? (
                  <div data-testid="legal-status-banner" className="flex items-center gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4">
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span className="text-sm font-bold text-red-800">{t("legalValidation.rejectedBanner")}</span>
                  </div>
                ) : (
                  <div data-testid="legal-status-banner" className="flex items-center gap-2.5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <span className="text-sm font-bold text-amber-800">{t("legalValidation.pendingBanner")}</span>
                  </div>
                )}

                <div className="space-y-2.5">
                  {legalDocTypes.map((dt) => {
                    const doc = docsByTypeFull[dt];
                    const required = requiredDocTypes.includes(dt);
                    return (
                      <div key={dt} data-testid={`legal-doc-${dt}`} className="border border-slate-200 rounded-xl p-3.5">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-[#1A1A1A] flex items-center gap-1.5">
                              {t(`supplierReg.docLabels.${dt}`)}
                              {!required && <span className="text-[10px] font-normal text-slate-400">({optionalMoralDocs.includes(dt) && esMoral ? t("legalValidation.moralTag") : t("legalValidation.optional")})</span>}
                            </div>
                            {doc && <div className="text-xs text-slate-500 truncate">{doc.filename}{doc.size ? ` · ${Math.round(doc.size / 1024)} KB` : ""}</div>}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {doc ? (
                              <span data-testid={`legal-doc-status-${dt}`} className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                                doc.status === "approved" ? "bg-emerald-100 text-emerald-700"
                                  : doc.status === "rejected" ? "bg-red-100 text-red-700"
                                    : "bg-amber-100 text-amber-700"}`}>
                                {t(`legalValidation.status.${doc.status || "pending"}`)}
                              </span>
                            ) : (
                              <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold ${optionalMoralDocs.includes(dt) && esMoral ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                                {optionalMoralDocs.includes(dt)
                                  ? (esMoral ? t("legalValidation.missingMoral")
                                    : rfcLen === 13 ? t("legalValidation.notApplicableFisica") : t("legalValidation.notLoaded"))
                                  : t("supplierDetail.pending")}
                              </span>
                            )}
                            {doc && (
                              <button data-testid={`legal-doc-view-${dt}`} onClick={() => viewDoc(doc.id)}
                                className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-bold">
                                <Download className="w-3 h-3" /> {t("legalValidation.viewDoc")}
                              </button>
                            )}
                          </div>
                        </div>

                        {doc?.status === "rejected" && doc.rejection_reason && (
                          <div className="mt-2 text-xs text-red-700 bg-red-50 rounded-lg px-2.5 py-1.5">{doc.rejection_reason}</div>
                        )}
                        {doc?.status === "approved" && doc.validated_at && (
                          <div className="mt-2 text-xs text-emerald-700">{t("legalValidation.approvedAt", { date: fmtDate(doc.validated_at) })}</div>
                        )}

                        {doc && (
                          <div className="mt-3 flex items-center gap-2">
                            {doc.status !== "approved" && (
                              <button data-testid={`legal-approve-${dt}`} disabled={validating === doc.id}
                                onClick={() => validateDoc(doc, true)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-xs font-bold disabled:opacity-50">
                                <CheckCircle2 className="w-3.5 h-3.5" /> {doc.status === "rejected" ? t("legalValidation.changeToApproved") : t("legalValidation.approve")}
                              </button>
                            )}
                            {doc.status !== "rejected" && (
                              <button data-testid={`legal-reject-${dt}`} disabled={validating === doc.id}
                                onClick={() => { setRejectDoc(doc); setRejectReason(""); }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-red-300 text-red-700 hover:bg-red-50 rounded-lg text-xs font-bold disabled:opacity-50">
                                <XCircle className="w-3.5 h-3.5" /> {doc.status === "approved" ? t("legalValidation.changeToRejected") : t("legalValidation.reject")}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Card 1 — Identidad */}
            <SectionCard testid="supplier-section-identity" title={t("supplierDetail.sections.identity")} icon={Building2}>
              <FieldRow label={t("supplierDetail.businessName")} value={s.business_name} />
              <FieldRow label={t("supplierDetail.fields.commercialName")} value={s.commercial_name} />
              <div className="sm:col-span-2 flex flex-wrap items-center gap-2">
                <span data-testid="supplier-detail-type-badge"
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                    isServ ? "bg-purple-100 text-purple-800" : "bg-blue-100 text-blue-800"
                  }`}>
                  {isServ ? t("suppliers.typeServicios") : t("suppliers.typeInsumos")}
                </span>
                <StatusBadge status={s.status === "approved" ? "approved_supplier" : s.status} />
                {s.legal_review_status && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-700">
                    {t("suppliers.legalLabel")}: {t(`suppliers.legal_${s.legal_parcial ? "partial" : s.legal_review_status}`, s.legal_review_status)}
                  </span>
                )}
              </div>
            </SectionCard>

            {/* Card 2 — Datos fiscales */}
            <SectionCard testid="supplier-section-fiscal" title={t("supplierDetail.sections.fiscal")} icon={FileText}>
              <FieldRow label={t("common.rfc")} value={s.rfc} mono />
              <FieldRow label={t("supplierDetail.address")} value={s.tax_address} wide />
              <FieldRow label={t("supplierDetail.fields.taxRegime")}
                value={s.tax_regime ? `${s.tax_regime}${taxRegimes[s.tax_regime] ? " — " + taxRegimes[s.tax_regime] : ""}` : "—"} wide />
              <FieldRow label={t("supplierDetail.fields.isrRate")}
                value={`${(Number(s.isr_withholding_rate || 0) * 100).toString()}%`} />
              <FieldRow label={t("supplierDetail.fields.ivaRate")}
                value={`${(Number(s.iva_withholding_rate || 0) * 100).toString()}%`} />
              {s.withholding_notes && (
                <FieldRow label={t("supplierDetail.fields.withholdingNotes")} value={s.withholding_notes} wide />
              )}
            </SectionCard>

            {/* Card 3 — Contacto comercial */}
            <SectionCard testid="supplier-section-contact" title={t("supplierDetail.sections.contact")} icon={Phone}>
              <FieldRow label={t("supplierDetail.fields.contactName")} value={s.contact_name} />
              <FieldRow label={t("common.phone")} value={s.contact_phone} />
              <FieldRow label={t("common.email")} value={s.contact_email} />
              <FieldRow label={t("supplierDetail.fields.website")}
                value={s.website ? <a href={s.website.startsWith("http") ? s.website : `https://${s.website}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{s.website}</a> : null} />
            </SectionCard>

            {/* Card 4 — Contacto de cobranza */}
            <SectionCard testid="supplier-section-billing" title={t("supplierDetail.sections.billing")} icon={Mail}>
              {(s.billing_contact_name || s.billing_contact_phone || s.billing_contact_email) ? (
                <>
                  <FieldRow label={t("supplierDetail.fields.billingContactName")} value={s.billing_contact_name} />
                  <FieldRow label={t("supplierDetail.fields.billingContactPhone")} value={s.billing_contact_phone} />
                  <FieldRow label={t("supplierDetail.fields.billingContactEmail")} value={s.billing_contact_email} />
                </>
              ) : (
                <div className="sm:col-span-2 text-sm text-slate-400">{t("supplierDetail.useCommercialContact")}</div>
              )}
            </SectionCard>

            {/* Card 5 — Términos comerciales y crédito */}
            <SectionCard testid="supplier-section-terms" title={t("supplierDetail.sections.terms")} icon={CreditCard}>
              <FieldRow label={t("common.creditDays")} value={String(s.credit_days ?? 0)} />
              <FieldRow label={t("supplierDetail.fields.hasCredit")} value={boolLabel(s.has_credit)} />
              <FieldRow label={t("supplierDetail.fields.paymentConditions")} value={s.payment_conditions} wide />
              <FieldRow label={t("supplierDetail.fields.deliveryTimeDescription")} value={s.delivery_time_description} wide />
            </SectionCard>

            {/* Card 6 — Datos bancarios */}
            <SectionCard testid="supplier-section-bank" title={t("supplierDetail.sections.bank")} icon={Banknote}>
              <FieldRow label={t("common.bank")} value={s.bank_name} />
              <FieldRow label="CLABE" value={maskClabe(s.bank_account)} mono />
            </SectionCard>

            {/* Card 7 — Cadencia de pedidos (insumos) */}
            {!isServ && (
              <SectionCard testid="supplier-section-orders" title={t("supplierDetail.sections.orders")} icon={ShoppingCart}>
                <FieldRow label={t("supplierDetail.fields.orderPeriod")} value={s.order_period} />
                <FieldRow label={t("supplierDetail.fields.orderDaySpecific")} value={s.order_day_specific} />
                <FieldRow label={t("supplierDetail.fields.deliveryDaySpecific")} value={s.delivery_day_specific} />
                <FieldRow label={t("supplierDetail.fields.orderDaysDescription")} value={s.order_days_description} wide />
              </SectionCard>
            )}

            {/* Card 8 — Servicios */}
            {isServ && (
              <SectionCard testid="supplier-section-services" title={t("supplierDetail.sections.services")} icon={Tag}>
                <FieldRow label={t("supplierReg.serviceModality")} value={s.service_modality ? t(`supplierReg.modalityOptions.${s.service_modality}`) : null} />
                <FieldRow label={t("supplierReg.serviceResponseTime")} value={s.service_response_time ? t(`supplierReg.responseTimeOptions.${s.service_response_time}`) : null} />
                <FieldRow label={t("supplierDetail.fields.hasActiveContract")} value={boolLabel(s.has_active_contract)} />
                <FieldRow label={t("supplierDetail.fields.attentionSchedule")} value={s.attention_schedule} />
                <FieldRow label={t("supplierDetail.fields.serviceDescription")} value={s.service_description} wide />
              </SectionCard>
            )}

            {/* Card 9 — Sucursales autorizadas */}
            <SectionCard testid="supplier-section-branches" title={t("supplierDetail.sections.branches")} icon={Building2}>
              <div className="sm:col-span-2 flex flex-wrap gap-2">
                {authorizedBranches.length === 0 && <span className="text-sm text-slate-400">—</span>}
                {authorizedBranches.map((b) => (
                  <span key={b.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 text-slate-700">
                    {b.name} <span className="font-mono text-[10px] text-slate-500">{branchCode(b)}</span>
                  </span>
                ))}
              </div>
            </SectionCard>

            {/* Card 9.5 — Operaciones por sucursal (multi-cuenta / multi-vendedor) */}
            {((s.branch_operations || []).length > 0 || user?.role === "corporate_admin") && (
              <SectionCard testid="supplier-section-branch-ops" title={t("supplierDetail.sections.branchOps")} icon={Store}>
                {!s.needs_branch_operations_setup && (s.branches_authorized || []).length >= 2 && (
                  <div data-testid="branch-ops-all-configured" className="sm:col-span-2 flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3.5 py-2.5 text-xs font-semibold">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> {t("supplierDetail.branchOpsBanner.allConfigured")}
                  </div>
                )}
                <div className="sm:col-span-2 -mx-2 overflow-x-auto">
                  <div className="flex flex-wrap items-center justify-between gap-2 px-2 mb-2">
                    <button data-testid="branch-ops-show-inactive-toggle" onClick={toggleShowInactiveOps}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border ${
                        showInactiveOps ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                      }`}>
                      <EyeOff className="w-3.5 h-3.5" /> {t("supplierDetail.branchOpsCols.showInactive")}
                    </button>
                    <div className="flex items-center gap-2">
                      {user?.role === "corporate_admin" && (
                        <button data-testid="branch-op-add-btn" onClick={() => { setPresetBranchId(null); setBranchOpModal(null); }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1A1A1A] hover:bg-black text-white rounded-lg text-xs font-bold">
                          <Plus className="w-3.5 h-3.5" /> {t("supplierEdit.branchOps.addButton")}
                        </button>
                      )}
                      <button data-testid="branch-ops-export-btn" onClick={exportBranchOpsXlsx}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold">
                        <Download className="w-3.5 h-3.5" /> {t("export.button")}
                      </button>
                    </div>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-100">
                        <th className="px-2 py-2">{t("supplierDetail.branchOpsCols.branch")}</th>
                        <th className="px-2 py-2">{t("supplierDetail.branchOpsCols.vendor")}</th>
                        <th className="px-2 py-2">{t("supplierDetail.branchOpsCols.phone")}</th>
                        <th className="px-2 py-2">{t("supplierDetail.branchOpsCols.email")}</th>
                        <th className="px-2 py-2">{t("supplierDetail.branchOpsCols.bank")}</th>
                        <th className="px-2 py-2">{t("supplierDetail.branchOpsCols.account")}</th>
                        <th className="px-2 py-2">{t("supplierDetail.branchOpsCols.clabe")}</th>
                        <th className="px-2 py-2">{t("supplierDetail.branchOpsCols.holder")}</th>
                        {user?.role === "corporate_admin" && <th className="px-2 py-2 text-right">{t("supplierDetail.branchOpsCols.actions")}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {(s.branch_operations || []).filter((op) => showInactiveOps || op.active !== false).map((op) => {
                        const br = branches.find((b) => b.id === op.branch_id);
                        const code = branchCode(br || {});
                        const inactive = op.active === false;
                        return (
                          <tr key={op.id} data-testid={`branch-ops-row-${code}`}
                            className={`border-b border-slate-50 last:border-0 ${inactive ? "opacity-40" : ""}`}>
                            <td className="px-2 py-2 font-bold">{code}{inactive && <span className="ml-1.5 text-[9px] font-semibold text-slate-400 uppercase">{t("supplierEdit.branchOps.inactiveBadge")}</span>}</td>
                            <td className="px-2 py-2">{op.vendor_name || "—"}</td>
                            <td className="px-2 py-2 text-slate-500">{op.vendor_phone || "—"}</td>
                            <td className="px-2 py-2 text-slate-500">{op.orders_email || op.vendor_email || "—"}</td>
                            <td className="px-2 py-2">{op.bank_name || "—"}</td>
                            <td className="px-2 py-2 font-mono text-xs">{op.bank_account_number || "—"}</td>
                            <td className="px-2 py-2 font-mono text-xs">{maskClabe(op.bank_clabe)}</td>
                            <td className="px-2 py-2 text-slate-500">{op.account_holder || "—"}</td>
                            {user?.role === "corporate_admin" && (
                              <td className="px-2 py-2 text-right whitespace-nowrap">
                                <button data-testid={`branch-ops-edit-${code}`} onClick={() => setBranchOpModal(op)}
                                  className="w-7 h-7 rounded-lg hover:bg-slate-100 inline-flex items-center justify-center text-slate-500">
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                {!inactive && (
                                  <button data-testid={`branch-ops-delete-${code}`} onClick={() => deactivateBranchOp(op, br?.name || code)}
                                    className="w-7 h-7 rounded-lg hover:bg-red-50 inline-flex items-center justify-center text-red-500">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                      {(s.branch_operations || []).filter((op) => showInactiveOps || op.active !== false).length === 0 && (
                        <tr>
                          <td colSpan={9} className="px-2 py-6 text-center text-sm text-slate-400">{t("supplierEdit.branchOps.empty")}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            )}

            {branchOpModal !== undefined && (
              <BranchOperationModal
                supplier={s}
                branches={branches}
                editingOp={branchOpModal}
                presetBranchId={presetBranchId}
                onClose={() => { setBranchOpModal(undefined); setPresetBranchId(null); }}
                onSaved={reloadSupplier}
              />
            )}


            {/* Card 10 — Documentos */}
            <SectionCard testid="supplier-section-documents" title={t("supplierDetail.sections.documents")} icon={FileText}>
              <div className="sm:col-span-2 space-y-2">
                {DOC_TYPES.map((dt) => {
                  const doc = docByType[dt];
                  return (
                    <div key={dt} className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-50 last:border-0">
                      <span className="text-sm text-slate-700">{t(`supplierReg.docLabels.${dt}`)}</span>
                      {doc ? (
                        canViewDocs ? (
                          <button data-testid={`supplier-doc-view-${dt}`} onClick={() => viewDoc(doc.document_id)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-bold">
                            <Download className="w-3 h-3" /> {t("common.view")}
                          </button>
                        ) : (
                          <span className="text-xs text-emerald-600 font-semibold">{doc.filename || t("common.uploaded")}</span>
                        )
                      ) : (
                        <span className="text-xs text-slate-400">{t("supplierDetail.pending")}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </SectionCard>

            {/* Card 11 — Operación como proveedor */}
            <SectionCard testid="supplier-section-operation" title={t("supplierDetail.sections.operation")} icon={History}>
              <FieldRow label={t("supplierDetail.fields.operationsStartDate")} value={s.operations_start_date} />
              <FieldRow label={t("supplierDetail.fields.bulkLoaded")} value={boolLabel(!!s.bulk_loaded)} />
              <FieldRow label={t("supplierDetail.fields.legalReviewedBy")} value={s.legal_reviewed_by} />
              <FieldRow label={t("supplierDetail.fields.approvedBy")} value={s.approved_by} />
            </SectionCard>

            {/* Card 12 — Link de completación (solo pending_documents) */}
            {isPendingDocs && user?.role === "corporate_admin" && (
              <SectionCard testid="supplier-section-completion-link" title={t("supplierDetail.sections.completionLink")} icon={Copy}>
                <div className="sm:col-span-2 space-y-3">
                  <div className="text-sm font-mono break-all bg-slate-50 rounded-lg px-3 py-2 text-slate-700">
                    {completionToken ? `${window.location.origin}/completar-registro/${completionToken}` : "—"}
                  </div>
                  <div className="flex gap-2">
                    <button data-testid="supplier-section-copy-link-btn" onClick={copyCompletionLink}
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#1A1A1A] text-white rounded-lg text-xs font-bold hover:bg-black">
                      <Copy className="w-3.5 h-3.5" /> {t("suppliers.copyCompletionLink")}
                    </button>
                    <button data-testid="supplier-section-regenerate-link-btn" onClick={regenerateLink}
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-orange-300 text-orange-800 rounded-lg text-xs font-bold hover:bg-orange-100">
                      <RefreshCw className="w-3.5 h-3.5" /> {t("suppliers.regenerateLink")}
                    </button>
                  </div>
                </div>
              </SectionCard>
            )}
          </div>
        )}

        {tab === "prices" && user?.role === "corporate_admin" && (
          <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="p-5 border-b border-slate-200 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-[#1A1A1A]" />
                  <div>
                    <div className="font-display font-bold text-base">{t("supplierDetail.currentCatalog")}</div>
                    <div className="text-xs text-slate-500">{t("supplierDetail.currentCatalogSub")}</div>
                  </div>
                </div>
                <button data-testid="supplier-link-supply-btn" onClick={() => setLinkOpen(true)}
                  className="px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 flex-shrink-0">
                  <Plus className="w-3.5 h-3.5" /> {t("catalog.linkSupply")}
                </button>
              </div>
              {supplies.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-500">{t("catalog.supplierEmptyCatalog")}</div>
              ) : (
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.supply")}</th>
                    <th className="text-left px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("supplierDetail.categoryCol")}</th>
                    <th className="text-right px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.price")}</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {supplies.map((rel) => (
                    <tr key={rel.supply_id} className="border-b border-slate-100 last:border-0" data-testid={`supplier-price-row-${rel.supply_id}`}>
                      <td className="px-5 py-3 text-sm font-semibold">{rel.supply?.name}</td>
                      <td className="px-5 py-3 text-xs text-slate-500 capitalize">{rel.supply?.category}</td>
                      <td className="px-5 py-3 text-sm font-mono font-bold text-right">{fmtMXN(rel.unit_price)}<div className="text-[10px] text-slate-400 font-normal">/{rel.supply?.unit}</div></td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => startEdit(rel)} data-testid={`edit-price-btn-${rel.supply_id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1A1A1A] text-white rounded-lg text-xs font-bold hover:bg-black">
                            <Pencil className="w-3 h-3" /> {t("common.edit")}
                          </button>
                          <button onClick={() => unlinkSupply(rel)} data-testid={`unlink-supply-btn-${rel.supply_id}`}
                            className="w-8 h-8 rounded-lg hover:bg-red-50 text-red-500 flex items-center justify-center">
                            <XIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="p-5 border-b border-slate-200 flex items-center gap-2">
                <History className="w-4 h-4 text-[#1A1A1A]" />
                <div>
                  <div className="font-display font-bold text-base">{t("supplierDetail.auditTitle")}</div>
                  <div className="text-xs text-slate-500">{t("supplierDetail.auditSub")}</div>
                </div>
              </div>
              {auditLogs.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-500">{t("supplierDetail.noChanges")}</div>
              ) : (
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("supplierDetail.auditDate")}</th>
                      <th className="text-left px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.supply")}</th>
                      <th className="text-right px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("supplierDetail.auditFromTo")}</th>
                      <th className="text-right px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("supplierDetail.auditPct")}</th>
                      <th className="text-left px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("supplierDetail.auditSource")}</th>
                      <th className="text-left px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("supplierDetail.auditUser")}</th>
                      <th className="text-left px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("supplierDetail.auditReason")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map((row) => {
                      const goingUp = row.change_percent >= 0;
                      const sourceLabels = {
                        po_override: t("supplierDetail.sourcePoOverride"),
                        corporate_edit: t("supplierDetail.sourceCorpEdit"),
                        supplier_proposal: t("supplierDetail.sourceSupplierProp"),
                      };
                      return (
                        <tr key={row.id} className="border-b border-slate-100 last:border-0">
                          <td className="px-5 py-3 text-xs text-slate-600 whitespace-nowrap">{fmtDate(row.created_at)}</td>
                          <td className="px-5 py-3 text-sm font-semibold">{row.supply_name}</td>
                          <td className="px-5 py-3 text-xs font-mono text-right whitespace-nowrap">
                            <span className="text-slate-400">{fmtMXN(row.old_price)}</span>
                            <span className="mx-1">→</span>
                            <span className="font-bold">{fmtMXN(row.new_price)}</span>
                          </td>
                          <td className="px-5 py-3 text-xs text-right">
                            <span className={`inline-flex items-center gap-0.5 font-bold ${goingUp ? "text-rose-600" : "text-emerald-600"}`}>
                              {goingUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                              {goingUp ? "+" : ""}{row.change_percent}%
                            </span>
                          </td>
                          <td className="px-5 py-3 text-xs">{sourceLabels[row.source] || row.source}{row.po_folio && <div className="text-[10px] text-slate-400 font-mono">{row.po_folio}</div>}</td>
                          <td className="px-5 py-3 text-xs">{row.changed_by_name}</td>
                          <td className="px-5 py-3 text-xs text-slate-600 max-w-xs truncate" title={row.reason}>{row.reason}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {tab === "invoices" && (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("po.folio")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("invoices.po")}</th>
                  <th className="text-right px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.total")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("invoices.dueDate")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.status")}</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-6 py-3 text-sm font-mono">{inv.folio_fiscal}</td>
                    <td className="px-6 py-3 text-sm font-mono">{inv.po_folio}</td>
                    <td className="px-6 py-3 text-sm font-bold text-right">{fmtMXN(inv.total)}</td>
                    <td className="px-6 py-3 text-sm">{fmtDate(inv.due_date)}</td>
                    <td className="px-6 py-3"><StatusBadge status={inv.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "pos" && (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("po.folio")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.branch")}</th>
                  <th className="text-right px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.total")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.status")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.date")}</th>
                </tr>
              </thead>
              <tbody>
                {purchaseOrders.map((po) => (
                  <tr key={po.id} onClick={() => navigate(`/ocs/${po.id}`)} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer">
                    <td className="px-6 py-3 text-sm font-mono font-bold">{po.folio}</td>
                    <td className="px-6 py-3 text-sm">{po.branch_name}</td>
                    <td className="px-6 py-3 text-sm font-bold text-right">{fmtMXN(po.total)}</td>
                    <td className="px-6 py-3"><StatusBadge status={po.status} /></td>
                    <td className="px-6 py-3 text-sm text-slate-500">{fmtDate(po.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "payments" && (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {payments.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">{t("supplierDetail.noPayments")}</div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.date")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("supplierDetail.tableInvoice")}</th>
                    <th className="text-right px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.amount")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("supplierDetail.tableReference")}</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100 last:border-0">
                      <td className="px-6 py-3 text-sm">{fmtDate(p.payment_date)}</td>
                      <td className="px-6 py-3 text-sm font-mono">{p.invoice.folio_fiscal}</td>
                      <td className="px-6 py-3 text-sm font-bold text-right">{fmtMXN(p.amount)}</td>
                      <td className="px-6 py-3 text-xs font-mono text-slate-500">{p.bank_reference}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {editing && (
        <div onClick={cancelEdit} data-testid="edit-price-modal-backdrop"
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-base">{t("supplierDetail.editPriceTitle")}</h3>
              <button onClick={cancelEdit} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center">
                <XIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <div className="text-[11px] uppercase font-bold text-slate-500">{t("common.supply")}</div>
              <div className="text-sm font-semibold mt-0.5">{editing.supply_name}</div>
              <div className="text-[11px] text-slate-500 mt-2">{t("supplierDetail.currentPrice")} <span className="font-mono font-bold text-[#1A1A1A]">{fmtMXN(editing.current_price)}</span></div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierDetail.newPriceLabel")}</label>
              <input data-testid="edit-price-new-input" type="number" step="0.01" min="0" value={editing.new_price}
                onChange={(e) => setEditing({ ...editing, new_price: parseFloat(e.target.value) || 0 })}
                className="mt-1.5 w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierDetail.reasonLabel")}</label>
              <textarea data-testid="edit-price-reason" rows={3} value={editing.reason}
                onChange={(e) => setEditing({ ...editing, reason: e.target.value })}
                placeholder={t("supplierDetail.reasonPlaceholder")}
                className="mt-1.5 w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={cancelEdit} className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50">{t("common.cancel")}</button>
              <button onClick={saveEdit} data-testid="edit-price-save-btn"
                className="btn-primary-chizy px-4 py-2 rounded-xl flex items-center gap-2">
                <Save className="w-3.5 h-3.5" /> {t("supplierDetail.saveAndLog")}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {editOpen && (
        <SupplierEditModal
          supplier={s}
          branches={branches}
          initialTab={editTab}
          onClose={() => { setEditOpen(false); setEditTab("identity"); }}
          onSaved={reloadSupplier}
        />
      )}

      {linkOpen && (
        <SupplierSupplyModal
          supplierId={id}
          existingSupplyIds={supplies.map((r) => r.supply_id)}
          onClose={() => setLinkOpen(false)}
          onLinked={loadPriceData}
        />
      )}

      {rejectDoc && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={() => setRejectDoc(null)}>
          <div data-testid="legal-reject-modal" className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-base text-[#1A1A1A] mb-1">{t("legalValidation.rejectModalTitle")}</h3>
            <p className="text-xs text-slate-500 mb-3">{t(`supplierReg.docLabels.${rejectDoc.doc_type}`)}</p>
            <textarea data-testid="legal-reject-reason" rows={3} value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={t("legalValidation.rejectReasonPlaceholder")}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-300" />
            <div className="flex gap-2 mt-4">
              <button data-testid="legal-reject-cancel" onClick={() => setRejectDoc(null)}
                className="flex-1 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50">{t("common.cancel")}</button>
              <button data-testid="legal-reject-confirm" disabled={rejectReason.trim().length < 5 || validating === rejectDoc.id}
                onClick={() => validateDoc(rejectDoc, false, rejectReason.trim())}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed">{t("legalValidation.confirmReject")}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
